import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  MenuItem,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Person,
  School,
  Work,
  Save,
  Add,
  Delete,
  ExpandMore,
  LocationOn,
  Phone,
  Email,
  DateRange,
  LinkedIn
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import dayjs from 'dayjs';
import MarkdownEditor from '../../../Resume/components/MarkdownEditor';

const ProfileUpdate = ({ candidateData, setCandidateData }) => {
  const [formData, setFormData] = useState(candidateData || {});
  const [expanded, setExpanded] = useState('personal');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [newEducation, setNewEducation] = useState({
    institution: '',
    degree: '',
    fieldOfStudy: '',
    startDate: '',
    endDate: '',
    description: ''
  });
  const [newExperience, setNewExperience] = useState({
    organization: '',
    designation: '',
    location: '',
    department: '',
    description: '',
    startDate: '',
    endDate: '',
    skills: [],
    duration: 0
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await axios.put(`/api/candidates/${candidateData._id}`, formData);
      setCandidateData(response.data);
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update profile. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = () => {
    if (!newSkill.trim()) return;
    
    const updatedSkills = [...(formData.skills || []), newSkill];
    setFormData(prev => ({
      ...prev,
      skills: updatedSkills
    }));
    setNewSkill('');
  };

  const handleDeleteSkill = (skillToDelete) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToDelete)
    }));
  };

  const handleAddEducation = () => {
    if (!newEducation.institution || !newEducation.degree) return;
    
    const updatedEducation = [...(formData.education || []), { ...newEducation, id: Date.now().toString() }];
    setFormData(prev => ({
      ...prev,
      education: updatedEducation
    }));
    setNewEducation({
      institution: '',
      degree: '',
      fieldOfStudy: '',
      startDate: '',
      endDate: '',
      description: ''
    });
  };

  const handleDeleteEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const handleAddExperience = () => {
    if (!newExperience.organization || !newExperience.designation) return;
    
    const updatedExperience = [...(formData.experience || []), { ...newExperience, id: Date.now().toString() }];
    setFormData(prev => ({
      ...prev,
      experience: updatedExperience
    }));
    setNewExperience({
      organization: '',
      designation: '',
      location: '',
      department: '',
      description: '',
      startDate: '',
      endDate: '',
      skills: [],
      duration: 0
    });
  };

  const handleDeleteExperience = (index) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }));
  };

  const handleExperienceChange = (field, value) => {
    setNewExperience(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEducationChange = (field, value) => {
    setNewEducation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateDuration = (start, end) => {
    if (!start) return 0;
    
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    
    const diffYears = endDate.getFullYear() - startDate.getFullYear();
    const diffMonths = endDate.getMonth() - startDate.getMonth();
    
    return diffYears + (diffMonths / 12);
  };

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography variant="h5" gutterBottom>
        Profile Information
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <Accordion 
          expanded={expanded === 'personal'} 
          onChange={handleAccordionChange('personal')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Person sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Personal Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gender"
                  name="gender"
                  value={formData.gender || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  select
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                  <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Date of Birth"
                    value={formData.DOB ? dayjs(formData.DOB) : null}
                    onChange={(date) => {
                      setFormData(prev => ({
                        ...prev,
                        DOB: date ? date.format('YYYY-MM-DD') : ''
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Current Location"
                  name="currentLocation"
                  value={formData.currentLocation || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="LinkedIn Profile"
                  name="linkedin"
                  value={formData.linkedin || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Headline"
                  name="headline"
                  value={formData.headline || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'professional'} 
          onChange={handleAccordionChange('professional')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Work sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Professional Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Current Company"
                  name="currentCompany"
                  value={formData.currentCompany || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Current Role"
                  name="currentRole"
                  value={formData.currentRole || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Experience (Years)"
                  name="experience_yrs"
                  type="number"
                  value={formData.experience_yrs || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Notice Period (Days)"
                  name="noticePeriod"
                  type="number"
                  value={formData.noticePeriod || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Current Compensation"
                  name="compensation"
                  type="number"
                  value={formData.compensation || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Expected Compensation"
                  name="expectation"
                  value={formData.expectation || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'skills'} 
          onChange={handleAccordionChange('skills')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <School sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Skills</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  fullWidth
                  label="Add a skill"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  variant="outlined"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddSkill();
                      e.preventDefault();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddSkill}
                  sx={{ ml: 1 }}
                >
                  <Add />
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.skills && formData.skills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    onDelete={() => handleDeleteSkill(skill)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {(!formData.skills || formData.skills.length === 0) && (
                  <Typography color="text.secondary">
                    No skills added yet. Add your key skills to improve your profile.
                  </Typography>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'experience'} 
          onChange={handleAccordionChange('experience')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Work sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Work Experience</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {formData.experience && formData.experience.map((exp, index) => (
                <Paper 
                  key={exp.id || index} 
                  elevation={1} 
                  sx={{ p: 2, mb: 2, position: 'relative' }}
                >
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={() => handleDeleteExperience(index)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {exp.designation} at {exp.organization}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {exp.startDate} - {exp.endDate || 'Present'} • {exp.location}
                      </Typography>
                    </Grid>
                    {exp.department && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          Department: {exp.department}
                        </Typography>
                      </Grid>
                    )}
                    {exp.description && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          {exp.description}
                        </Typography>
                      </Grid>
                    )}
                    {exp.skills && exp.skills.length > 0 && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {exp.skills.map((skill, i) => (
                            <Chip key={i} label={skill} size="small" />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Add New Experience
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company/Organization"
                    value={newExperience.organization}
                    onChange={(e) => handleExperienceChange('organization', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Position/Designation"
                    value={newExperience.designation}
                    onChange={(e) => handleExperienceChange('designation', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={newExperience.location}
                    onChange={(e) => handleExperienceChange('location', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Department"
                    value={newExperience.department}
                    onChange={(e) => handleExperienceChange('department', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date (MM/YYYY)"
                    value={newExperience.startDate}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleExperienceChange('startDate', value);
                      
                      // Calculate duration if both dates are available
                      if (value && newExperience.endDate) {
                        const duration = calculateDuration(value, newExperience.endDate);
                        handleExperienceChange('duration', duration);
                      }
                    }}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End Date (MM/YYYY or leave blank if current)"
                    value={newExperience.endDate}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleExperienceChange('endDate', value);
                      
                      // Calculate duration if start date is available
                      if (newExperience.startDate) {
                        const duration = calculateDuration(newExperience.startDate, value || null);
                        handleExperienceChange('duration', duration);
                      }
                    }}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Description
                  </Typography>
                  <MarkdownEditor
                    value={newExperience.description || ''}
                    onChange={(content) => handleExperienceChange('description', content)}
                    minHeight={150}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddExperience}
                    startIcon={<Add />}
                    disabled={!newExperience.organization || !newExperience.designation}
                  >
                    Add Experience
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded === 'education'} 
          onChange={handleAccordionChange('education')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <School sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Education</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {formData.education && formData.education.map((edu, index) => (
                <Paper 
                  key={edu.id || index} 
                  elevation={1} 
                  sx={{ p: 2, mb: 2, position: 'relative' }}
                >
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={() => handleDeleteEducation(index)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {edu.degree}
                        {edu.fieldOfStudy && ` in ${edu.fieldOfStudy}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1">
                        {edu.institution}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {edu.startDate} - {edu.endDate || 'Present'}
                      </Typography>
                    </Grid>
                    {edu.description && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          {edu.description}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Add New Education
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Institution"
                    value={newEducation.institution}
                    onChange={(e) => handleEducationChange('institution', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Degree"
                    value={newEducation.degree}
                    onChange={(e) => handleEducationChange('degree', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Field of Study"
                    value={newEducation.fieldOfStudy}
                    onChange={(e) => handleEducationChange('fieldOfStudy', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date (MM/YYYY)"
                    value={newEducation.startDate}
                    onChange={(e) => handleEducationChange('startDate', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End Date (MM/YYYY or leave blank if current)"
                    value={newEducation.endDate}
                    onChange={(e) => handleEducationChange('endDate', e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={newEducation.description}
                    onChange={(e) => handleEducationChange('description', e.target.value)}
                    variant="outlined"
                    margin="normal"
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddEducation}
                    startIcon={<Add />}
                    disabled={!newEducation.institution || !newEducation.degree}
                  >
                    Add Education
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          startIcon={<Save />}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Profile'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileUpdate;
